import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Table,
    Button,
    Form,
    Modal,
    Spinner,
    Alert,
    Badge
} from 'react-bootstrap';
import { CirclePlus, Trash2 } from 'lucide-react';
import {
    useLazyGetRemindersQuery,
    useInsertReminderMutation,
    useDeleteReminderMutation
} from '../app/api';
import { selectAdminUuid } from '../features/auth/authSlice';

export default function Cron() {
    const [getReminders] = useLazyGetRemindersQuery();
    const [insertReminder] = useInsertReminderMutation();
    const [deleteReminder] = useDeleteReminderMutation();
    const adminUUID = useSelector(selectAdminUuid);

    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addData, setAddData] = useState({
        reminder_date: '',
        emails: '',
        message: ''
    });

    const fetchReminders = async () => {
        setLoading(true);
        const res = await getReminders();
        if (res.data) {
            setReminders(res.data);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchReminders();
    }, []);

    const handleAdd = async () => {
        if (!addData.reminder_date || !addData.emails || !addData.message) {
            alert('All fields required');
            return;
        }
        setSaving(true);
        const res = await insertReminder({
            reminder_date: addData.reminder_date,
            emails: addData.emails,
            message: addData.message,
            created_by: adminUUID
        });
        setSaving(false);
        if (res.data && res.data.inserted) {
            setAddData({ reminder_date: '', emails: '', message: '' });
            setShowAddModal(false);
            fetchReminders();
        } else {
            alert('There was an error processing your request.');
        }
    };

    const handleDelete = async (id) => {
        if (confirm('Are you sure you want to delete this reminder?')) {
            const res = await deleteReminder({ id });
            if (res.data && res.data.deleted) {
                fetchReminders();
            } else {
                alert('There was an error processing your request.');
            }
        }
    };

    const isDueSoon = (dateStr) => {
        const diff = new Date(dateStr) - new Date();
        return diff >= 0 && diff < 7 * 24 * 60 * 60 * 1000;
    };

    const AddReminderModal =
    <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
        <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="h4">Add Reminder</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
            <Form>
                <Form.Group>
                    <Form.Label>Reminder Date:</Form.Label>
                    <Form.Control
                        type="date"
                        className="mb-3"
                        value={addData.reminder_date}
                        onChange={(e) => setAddData(p => ({ ...p, reminder_date: e.target.value }))}
                    />

                    <Form.Label>Emails (comma separated):</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        className="mb-3"
                        placeholder="1@alltheyears.com, 2@alltheyears.com"
                        value={addData.emails}
                        onChange={(e) => setAddData(p => ({ ...p, emails: e.target.value }))}
                    />

                    <Form.Label>Message:</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        className="mb-3"
                        placeholder="Enter reminder message to be sent via email..."
                        value={addData.message}
                        onChange={(e) => setAddData(p => ({ ...p, message: e.target.value }))}
                    />
                </Form.Group>
            </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                Cancel
            </Button>
            {!saving &&
                <Button variant="success" onClick={handleAdd}>Add Reminder</Button>
            }
            {saving &&
                <Spinner animation="border" />
            }
        </Modal.Footer>
        </div>
    </Modal>;

    return (
        <>
            <Container className="p-4">
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h4 className="mb-0">Reminders</h4>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" className="d-flex align-items-center" onClick={() => setShowAddModal(true)}>
                            <CirclePlus className="me-2" size={18} /> Add Reminder
                        </Button>
                    </Col>
                </Row>

                {loading &&
                    <div className="d-flex align-items-center justify-content-center my-4">
                        <Spinner animation="border" className="me-3" />
                        <span><strong>Loading...</strong></span>
                    </div>
                }

                {!loading && reminders.length === 0 &&
                    <Alert variant="secondary">No upcoming reminders.</Alert>
                }

                {!loading && reminders.length > 0 &&
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Emails</th>
                                <th>Message</th>
                                <th>Created</th>
                                <th style={{ width: '80px' }}>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reminders.map((r, i) => (
                                <tr key={"rem_" + i} className={isDueSoon(r.reminder_date) ? "table-warning" : ""}>
                                    <td>
                                        {new Date(r.reminder_date).toLocaleDateString()}
                                        {isDueSoon(r.reminder_date) && <Badge bg="warning" className="ms-2">Soon</Badge>}
                                    </td>
                                    <td>{r.emails}</td>
                                    <td>{r.message}</td>
                                    <td>{new Date(r.created).toLocaleDateString()}</td>
                                    <td>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(r.id)}>
                                            <Trash2 size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                }
            </Container>

            <h4 className="ps-4">&nbsp; Cron Task Monitor</h4>
            <iframe
                src="/flower/"
                style={{ width: '100%', height: '800px', border: 'none' }}
                title="Celery Flower"
            />

            {AddReminderModal}
        </>
    );
}
